import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { errore } from "@/pages/crm/crmComuni";
import { TIPI_EVENTO, fmtEvento, perInput } from "./calendarioComuni";

// Sprint CRM-5 — «Fine» come durata dall'inizio; «personalizzata» = fine scritta a mano
const DURATE = [["15", "15 min"], ["30", "30 min"], ["60", "1 ora"], ["90", "1 ora e mezza"], ["120", "2 ore"], ["180", "3 ore"],
  ["giorno", "Tutto il giorno"], ["altro", "Personalizzata"]];
const piu = (inizio, min) => {
  const d = new Date(inizio); if (Number.isNaN(d.getTime())) return "";
  d.setMinutes(d.getMinutes() + min);
  const z = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
};
export const fineDaDurata = (inizio, durata) => (/^\d+$/.test(durata) ? piu(inizio, Number(durata)) : "");
export const durataDi = (f) => {
  if (f.tutto_il_giorno) return "giorno";
  if (!f.fine) return "altro";
  const m = Math.round((new Date(f.fine) - new Date(f.inizio)) / 60000);
  return DURATE.some(([k]) => k === String(m)) ? String(m) : "altro";
};

// Inizio + durata (+ fine a mano con «personalizzata»); valore = { inizio, fine, tutto_il_giorno, durata }
function InizioFine({ v, onCambia, prefix }) {
  const cambia = (x) => {
    const n = { ...v, ...x };
    n.tutto_il_giorno = n.durata === "giorno";
    if (n.tutto_il_giorno) { n.inizio = `${(n.inizio || "").slice(0, 10)}T00:00`; n.fine = ""; }
    else if (n.durata !== "altro") n.fine = fineDaDurata(n.inizio, n.durata);
    onCambia(n);
  };
  return (
    <div className="grid grid-cols-2 gap-2">
      <div><Label>Inizio *</Label><Input type={v.tutto_il_giorno ? "date" : "datetime-local"} value={v.tutto_il_giorno ? (v.inizio || "").slice(0, 10) : v.inizio}
        onChange={(e) => cambia({ inizio: v.tutto_il_giorno ? `${e.target.value}T00:00` : e.target.value })} data-testid={`${prefix}-inizio`} /></div>
      <div><Label>Fine</Label>
        <Select value={v.durata} onValueChange={(d) => cambia({ durata: d })}>
          <SelectTrigger data-testid={`${prefix}-durata`}><SelectValue /></SelectTrigger>
          <SelectContent>{DURATE.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select></div>
      {v.durata === "altro" && <div className="col-span-2"><Label>Fine (a mano)</Label>
        <Input type="datetime-local" value={v.fine || ""} onChange={(e) => onCambia({ ...v, fine: e.target.value })} data-testid={`${prefix}-fine`} /></div>}
    </div>
  );
}

// scelte: [{ chiave, etichetta, calendario, company_id, site_id, proprietario_id }]
export function NuovoEvento({ scelte, iniziale, opportunita, onChiudi, onFatto }) {
  const [f, setF] = useState({ chiave: scelte[0]?.chiave || "", tipo: "appuntamento", titolo: "", inizio: iniziale || "",
    fine: iniziale ? fineDaDurata(iniziale, "30") : "", tutto_il_giorno: false, descrizione: "", durata: "30" });
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const salva = async () => {
    const c = scelte.find((x) => x.chiave === f.chiave);
    try {
      await api.post("/calendario/eventi", { calendario: c.calendario, company_id: c.company_id, site_id: c.site_id,
        proprietario_id: c.proprietario_id, tipo: f.tipo, titolo: f.titolo, descrizione: f.descrizione, inizio: f.inizio,
        fine: f.fine || null, tutto_il_giorno: f.tutto_il_giorno, opportunita_id: opportunita?.id || null });
      toast.success("Evento creato"); onFatto();
    } catch (e) { toast.error(errore(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cal-nuovo-dialog">
        <DialogHeader><DialogTitle>{opportunita ? "Nuova attività" : "Nuovo evento"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {opportunita && <div className="text-sm text-slate-600" data-testid="cal-nuovo-opportunita">Collegata a: <b>{opportunita.descrizione}</b></div>}
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Calendario</Label>
              <Select value={f.chiave} onValueChange={(v) => set("chiave", v)}>
                <SelectTrigger data-testid="cal-nuovo-calendario"><SelectValue /></SelectTrigger>
                <SelectContent>{scelte.map((c) => <SelectItem key={c.chiave} value={c.chiave}>{c.etichetta}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label>Tipo</Label>
              <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
                <SelectTrigger data-testid="cal-nuovo-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(TIPI_EVENTO).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><Label>Titolo *</Label><Input value={f.titolo} onChange={(e) => set("titolo", e.target.value)} data-testid="cal-nuovo-titolo" /></div>
          <InizioFine v={f} onCambia={(n) => setF((x) => ({ ...x, ...n }))} prefix="cal-nuovo" />
          <div><Label>Descrizione</Label><Textarea value={f.descrizione} onChange={(e) => set("descrizione", e.target.value)} data-testid="cal-nuovo-descrizione" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onChiudi} data-testid="cal-nuovo-annulla">Annulla</Button>
          <Button onClick={salva} disabled={!f.titolo.trim() || !f.inizio || !f.chiave} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="cal-nuovo-salva">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SchedaEvento({ ev, onChiudi, onFatto }) {
  const confirm = useConfirm();
  const [esito, setEsito] = useState("");
  const [mod, setMod] = useState(null);
  const azione = async (fn, msg) => { try { await fn(); toast.success(msg); onFatto(); } catch (e) { toast.error(errore(e)); } };
  const elimina = async () => {
    if (await confirm({ title: "Elimina evento", message: `Eliminare «${ev.titolo}»?`, confirmLabel: "SI, elimina" })) {
      azione(() => api.delete(`/calendario/eventi/${ev.id}`), "Evento eliminato");
    }
  };
  const [esitoMod, setEsitoMod] = useState(null);  // Sprint CRM-5 — esito correggibile anche a evento fatto
  const { durata, ...campi } = mod || {};
  const salva = () => azione(() => api.put(`/calendario/eventi/${ev.id}`, { ...campi, fine: campi.fine || null,
    originali: { titolo: ev.titolo, inizio: perInput(ev.inizio), fine: ev.fine ? perInput(ev.fine) : null, tutto_il_giorno: !!ev.tutto_il_giorno,
      descrizione: ev.descrizione } }), "Evento modificato");
  const apriModifica = () => {
    const base = { titolo: ev.titolo, inizio: perInput(ev.inizio), fine: ev.fine ? perInput(ev.fine) : "", tutto_il_giorno: !!ev.tutto_il_giorno, descrizione: ev.descrizione };
    setMod({ ...base, durata: durataDi(base) });
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onChiudi(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cal-scheda-dialog">
        <DialogHeader><DialogTitle data-testid="cal-scheda-titolo">{ev.titolo}</DialogTitle></DialogHeader>
        {mod ? (
          <div className="space-y-2">
            <Input value={mod.titolo} onChange={(e) => setMod({ ...mod, titolo: e.target.value })} data-testid="cal-mod-titolo" />
            <InizioFine v={mod} onCambia={setMod} prefix="cal-mod" />
            <Textarea value={mod.descrizione || ""} onChange={(e) => setMod({ ...mod, descrizione: e.target.value })} data-testid="cal-mod-descrizione" />
          </div>
        ) : (
          <div className="space-y-1 text-sm" data-testid="cal-scheda-dati">
            <div>{TIPI_EVENTO[ev.tipo]} · {fmtEvento(ev)} · {ev.calendario === "sede" ? `Sede ${ev.sede_nome || "—"}` : ev.proprietario_nome}</div>
            {ev.assegnato_da_nome && <div className="text-slate-500">Assegnato da {ev.assegnato_da_nome}</div>}
            {ev.descrizione && <div className="whitespace-pre-wrap rounded-md border border-slate-300 bg-slate-50 px-3 py-2 my-1" data-testid="cal-scheda-descrizione">{ev.descrizione}</div>}
            {ev.opportunita_id && <Link className="text-blue-600 font-medium hover:underline" to={`/crm/opportunita/${ev.opportunita_id}`}
              data-testid="cal-scheda-opportunita">Opportunità: {ev.opportunita_descrizione || "apri"}</Link>}
            {ev.fatto ? <div className="space-y-2">
              <div className="text-emerald-700" data-testid="cal-scheda-fatto">Fatto{ev.esito ? ` · ${ev.esito}` : ""}</div>
              {ev.puo_modificare && (esitoMod === null
                ? <div className="flex gap-2"><Button size="sm" variant="outline" onClick={() => azione(() => api.post(`/calendario/eventi/${ev.id}/riapri`), "Evento riaperto")}
                    data-testid="cal-scheda-riapri">Riapri</Button>
                  <Button size="sm" variant="outline" onClick={() => setEsitoMod(ev.esito || "")} data-testid="cal-scheda-modifica-esito">Modifica esito</Button></div>
                : <div className="flex gap-2"><Input value={esitoMod} onChange={(e) => setEsitoMod(e.target.value)} data-testid="cal-scheda-esito-nuovo" />
                  <Button size="sm" onClick={() => azione(() => api.post(`/calendario/eventi/${ev.id}/fatto`, { esito: esitoMod }), "Esito modificato")}
                    data-testid="cal-scheda-esito-salva">Salva esito</Button></div>)}
            </div>
              : ev.puo_modificare && <div className="flex gap-2 pt-2"><Input placeholder="Esito (facoltativo)" value={esito} onChange={(e) => setEsito(e.target.value)} data-testid="cal-scheda-esito" />
                <Button onClick={() => azione(() => api.post(`/calendario/eventi/${ev.id}/fatto`, { esito }), "Segnato fatto")} data-testid="cal-scheda-segna-fatto">Segna fatto</Button></div>}
          </div>
        )}
        {/* Sprint CRM-2C — senza permesso di scrittura i comandi non compaiono (il server risponde 403) */}
        {ev.puo_modificare && <DialogFooter>
          <Button variant="outline" onClick={elimina} className="text-rose-700" data-testid="cal-scheda-elimina">Elimina</Button>
          {mod ? <Button onClick={salva} data-testid="cal-mod-salva">Salva</Button>
            : <Button variant="outline" onClick={apriModifica} data-testid="cal-scheda-modifica">Modifica</Button>}
        </DialogFooter>}
      </DialogContent>
    </Dialog>
  );
}
